import { createContext, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import type {
  PageOperationType,
  PendingUiOperation,
  UiOperation,
  UiOperationResult,
  UiOperationSource,
} from '@/services/uiActions/operations'
import { errorResult, isPageOperation, runPageOperation, successResult, unsupported } from '@/services/uiActions/operations'
import type { ResourceDescriptor } from '@/services/uiActions/resources'
import { resolveOperationRoute } from '@/services/uiActions/resources'
import type { PageRoutine, RoutineSource } from '@/services/uiActions/routine'
import { createRoutineRegistry } from '@/services/uiActions/routineRegistry'

export type UiActionStatus = 'idle' | 'running' | 'complete' | 'error'

export type ToolCallResolver = {
  approve: (source: UiOperationSource) => Promise<void>
  reject: (source: UiOperationSource, reason?: string) => Promise<void>
}

type UiActionRunnerContextValue = {
  status: UiActionStatus
  pending: PendingUiOperation | null
  result: UiOperationResult | null
  linkedSource: UiOperationSource | null
  submit: (operation: UiOperation, source?: UiOperationSource) => Promise<UiOperationResult>
  settle: (pending: PendingUiOperation, work: () => Promise<unknown> | unknown) => Promise<void>
  commit: () => Promise<UiOperationResult>
  abandon: (reason?: string) => Promise<UiOperationResult>
  clear: () => void
  registerRoutine: (source: RoutineSource) => () => void
  registerToolCallResolver: (resolver: ToolCallResolver) => () => void
  getRoutine: () => PageRoutine | undefined
  /** Resolves once this tool call's operation is done; true when there was one to wait for. */
  awaitOperationFor: (sourceId: string) => Promise<boolean>
  /** Read through a ref so it stays correct after an await, unlike the `linkedSource` state. */
  isLinkedTo: (sourceId: string) => boolean
}

export const UiActionRunnerContext = createContext<UiActionRunnerContextValue | null>(null)

export function UiActionProvider({ children, resources = [] }: { children: ReactNode; resources?: readonly ResourceDescriptor[] }) {
  const navigate = useNavigate()
  const routineRegistryRef = useRef(createRoutineRegistry())
  const resolveRef = useRef<(result: UiOperationResult) => void>()
  const pendingRef = useRef<PendingUiOperation | null>(null)
  const pendingPromiseRef = useRef<Promise<UiOperationResult>>()
  const resolverRef = useRef<ToolCallResolver>()
  const linkedRef = useRef<UiOperationSource | null>(null)
  const [status, setStatus] = useState<UiActionStatus>('idle')
  const [pending, setPending] = useState<PendingUiOperation | null>(null)
  const [result, setResult] = useState<UiOperationResult | null>(null)
  const [linkedSource, setLinkedSource] = useState<UiOperationSource | null>(null)

  const registerRoutine = useCallback((source: RoutineSource) => routineRegistryRef.current.register(source), [])

  const getRoutine = useCallback(() => routineRegistryRef.current.current(), [])

  const registerToolCallResolver = useCallback((resolver: ToolCallResolver) => {
    resolverRef.current = resolver
    return () => {
      if (resolverRef.current === resolver) resolverRef.current = undefined
    }
  }, [])

  const link = useCallback((source: UiOperationSource | null) => {
    linkedRef.current = source
    setLinkedSource(source)
  }, [])

  const isSavePending = useCallback(() => {
    const routine = routineRegistryRef.current.current()
    if (!routine?.save) return false

    try {
      return routine.status().dirty
    } catch {
      return false
    }
  }, [])

  const settleToolCall = useCallback((work?: Promise<void>) => {
    // The chat reports its own failures; an unhandled rejection here would only add noise.
    void work?.catch(() => undefined)
  }, [])

  const finish = useCallback(
    (next: UiOperationResult, claimed?: PendingUiOperation, savePending = false) => {
      pendingRef.current = null
      setResult(next)
      setStatus(next.status === 'success' ? 'complete' : 'error')
      setPending(null)

      const source = claimed?.source
      if (source) {
        if (next.status === 'error') {
          settleToolCall(resolverRef.current?.reject(source, next.message))
        } else if (savePending && !isPageOperation(claimed.operation.type)) {
          link(source)
        } else {
          settleToolCall(resolverRef.current?.approve(source))
        }
      }

      resolveRef.current?.(next)
      resolveRef.current = undefined
    },
    [link, settleToolCall]
  )

  const submit = useCallback(
    (operation: UiOperation, source?: UiOperationSource) => {
      const route = resolveOperationRoute(resources, operation)

      if (!route && !routineRegistryRef.current.current()) {
        const refused = errorResult(unsupported(`No page can perform ${operation.type}`))
        setResult(refused)
        setStatus('error')
        if (source) settleToolCall(resolverRef.current?.reject(source, refused.message))
        return Promise.resolve(refused)
      }

      const next: PendingUiOperation = { operation, source }
      pendingRef.current = next
      setResult(null)
      setStatus('running')
      setPending(next)

      if (route) navigate(route)

      const settled = new Promise<UiOperationResult>(resolve => {
        resolveRef.current = resolve
      })
      pendingPromiseRef.current = settled

      return settled
    },
    [navigate, resources, settleToolCall]
  )

  const awaitOperationFor = useCallback(async (sourceId: string): Promise<boolean> => {
    if (pendingRef.current?.source?.id !== sourceId) return false

    await pendingPromiseRef.current
    return true
  }, [])

  const isLinkedTo = useCallback((sourceId: string) => linkedRef.current?.id === sourceId, [])

  const settle = useCallback(
    async (claimed: PendingUiOperation, work: () => Promise<unknown> | unknown) => {
      if (pendingRef.current !== claimed) return

      try {
        const value = await work()
        // Read before the page can navigate away: an operation that left the form dirty is waiting
        // on a save, and one that did not has already done everything it was going to do.
        if (pendingRef.current === claimed) finish(successResult(value), claimed, isSavePending())
      } catch (error) {
        if (pendingRef.current === claimed) finish(errorResult(error), claimed)
      }
    },
    [finish, isSavePending]
  )

  const commit = useCallback(async (): Promise<UiOperationResult> => {
    const routine = routineRegistryRef.current.current()
    const source = linkedRef.current

    // A link only survives while a save is outstanding, so approving without one would tell the
    // model the browser saved something it never saved.
    if (source && !routine?.save) {
      return errorResult(unsupported('The page holding this change is no longer open'))
    }

    let saved: unknown
    try {
      if (routine?.save) saved = await routine.save()
    } catch (error) {
      return errorResult(error)
    }

    if (!source) return successResult(saved)

    link(null)
    try {
      await resolverRef.current?.approve(source)
    } catch (error) {
      return errorResult(error)
    }

    return successResult(saved)
  }, [link])

  const abandon = useCallback(
    async (reason?: string): Promise<UiOperationResult> => {
      const routine = routineRegistryRef.current.current()

      try {
        await routine?.discard?.()
      } catch (error) {
        return errorResult(error)
      }

      const source = linkedRef.current
      if (!source) return successResult(undefined)

      link(null)
      try {
        await resolverRef.current?.reject(source, reason)
      } catch (error) {
        return errorResult(error)
      }

      return successResult(undefined)
    },
    [link]
  )

  const clear = useCallback(() => {
    pendingRef.current = null
    setStatus('idle')
    setPending(null)
    setResult(null)
    link(null)
    resolveRef.current = undefined
  }, [link])

  useEffect(() => {
    if (!pending || !isPageOperation(pending.operation.type)) return

    const routine = routineRegistryRef.current.current()
    if (!routine) return

    void settle(pending, () => runPageOperation(pending.operation.type as PageOperationType, routine))
  }, [pending, settle])

  const value = useMemo(
    () => ({
      status,
      pending,
      result,
      linkedSource,
      submit,
      settle,
      commit,
      abandon,
      clear,
      registerRoutine,
      registerToolCallResolver,
      getRoutine,
      awaitOperationFor,
      isLinkedTo,
    }),
    [
      abandon,
      awaitOperationFor,
      clear,
      commit,
      getRoutine,
      isLinkedTo,
      linkedSource,
      pending,
      registerRoutine,
      registerToolCallResolver,
      result,
      settle,
      status,
      submit,
    ]
  )

  return <UiActionRunnerContext.Provider value={value}>{children}</UiActionRunnerContext.Provider>
}
